import "@westpierstudio/luma";
import "@westpierstudio/core-module-oryx";

import * as PIXI from "pixi.js";
import { ApplicationManager } from "@westpierstudio/luma/application";
import { AudioManager } from "@westpierstudio/luma/audio";
import { PixiFactory } from "@westpierstudio/luma/factory";
import { DefaultLayout } from "@westpierstudio/luma/layout";
import { ResourcesManager } from "@westpierstudio/luma/resources";
import { EventEmitter } from "@westpierstudio/luma/events";
import { constants } from "@westpierstudio/luma/utils";
import { KSGLoadingScreen } from "@westpierstudio/core-module-oryx/game/KSGLoadingScreen";
import { DragonsVsPandasSplashScreen } from "game/DragonsVsPandasSplashScreen";
import { DragonsVsPandasGame } from "game/DragonsVsPandasGame";
import { DragonsVsPandasGameClient } from "game/DragonsVsPandasGameClient";
import { DragonsVsPandasGameController } from "game/DragonsVsPandasGameController";
import { DragonsVsPandasGameModel } from "game/DragonsVsPandasGameModel";
import { DragonsVsPandasGameView } from "game/DragonsVsPandasGameView";
import { GameServices } from "game/GameServices";
import { Comms, CurrencyService, Gamebridge, RGSData, UILayout, UIMessages, Analytics, DebugService } from "@westpierstudio/core-module-oryx/services";
import { WebSocketDiscoveryProtocol } from "@westpierstudio/core-module-oryx/services/comms";
import { createLocalStorageService } from "@westpierstudio/core-module-oryx/services/localstorage";
import { utils } from "pixi.js";
import { createTextureScaleStrategy } from "@westpierstudio/core-module-oryx/services/layout/TextureScaleStrategy";
import { DragonsVsPandasLoading } from "game/DragonsVsPandasLoading";
import { DragonsVsPandsPaytableContentFactoryService } from "game/paytable/DragonVsPandasPaytableContentFactoryService";
import { iosNative } from "@westpierstudio/core-module-oryx/utils";
import { PlayerInactivityService } from "@westpierstudio/core-module-oryx/services/playerinactivity";

const development = process.env.NODE_ENV !== `production`;

if (!development) {
    utils.skipHello();
    if (iosNative) {
        console.log(`ios-native`);
    }
} else {
    console.log(`### Build Time: ${constants.buildTime} ###`);
    console.log(`### Use Consolidated Json: ${constants.consolidateJson} ###`);
    console.log(`### Core Version: ${constants.coreVersion} ###`);
    console.log(`### Game Version: ${constants.gameVersion} ###`);
    console.log(`### Core Pixi versions ###`);
    Object.entries(constants.corePixiVersions).forEach(([dependency, version]) => console.log(dependency, version));
    console.log(`#########`);
}

const rgsData = new RGSData({
    gameCode: development ? `BGS_KSG_DRAGONS_VS_PANDAS_88_SPREAD` : undefined
});

const textureScaleStrategy = createTextureScaleStrategy();
textureScaleStrategy.debug = development;

const resources = new ResourcesManager();
const application = new ApplicationManager();
const layout = new DefaultLayout(application, textureScaleStrategy);
const loadScreenLayout = new DefaultLayout(application, textureScaleStrategy);
const splashScreenLayout = new DefaultLayout(application, textureScaleStrategy);
const uiLayout = new UILayout(application, textureScaleStrategy);
const factory = new PixiFactory(resources);
const audio = new AudioManager(application);
const loader = new DragonsVsPandasLoading(resources, layout, audio, rgsData);
const paytableContentFactory = new DragonsVsPandsPaytableContentFactoryService();

const webSocketDiscoveryProtocol = new WebSocketDiscoveryProtocol(rgsData.gameServerURLs);
const adminWebSocketDiscoveryProtocol = new WebSocketDiscoveryProtocol(rgsData.gameServerURLs.map(url => {
    return url.replace(`23011`, `23012`).replace(`game_ws`, `admin_ws`);
}));

const debug = new DebugService();
const comms = new Comms(rgsData, webSocketDiscoveryProtocol);
const adminComms = new Comms(rgsData, adminWebSocketDiscoveryProtocol);
const gameBridge = new Gamebridge();
const gameLocalStorage = createLocalStorageService(`ts-dragons-vs-pandas`, `westpier-studio`);
const currency = new CurrencyService(rgsData.playMode, rgsData.languageCode, debug);
const analytics = new Analytics(comms, rgsData, layout, application);
const uiMessages = new UIMessages(resources);
const playerInactivity = new PlayerInactivityService(debug, rgsData);

analytics.debug.useNetwork = !development;
analytics.debug.logging = false;
analytics.debug.unpackContent = development;

layout.setBucket({ width: 320, height: 200 });

comms.enableLogging = development;
adminComms.enableLogging = development;
gameBridge.enableLogging = development;

const services = new GameServices(
    layout,
    resources,
    application,
    factory,
    loader,
    audio,
    rgsData,
    comms,
    adminComms,
    gameBridge,
    uiLayout,
    gameLocalStorage,
    currency,
    loadScreenLayout,
    splashScreenLayout,
    analytics,
    uiMessages,
    debug,
    playerInactivity,
    paytableContentFactory
);

const gameView = new DragonsVsPandasGameView(services);
const gameModel = new DragonsVsPandasGameModel(services);
const gameClient = new DragonsVsPandasGameClient(services);
const gameEmitter = new EventEmitter();

const gameController = new DragonsVsPandasGameController(
    services,
    gameView,
    gameModel,
    gameEmitter,
    gameClient
);

const loadingScreen = new KSGLoadingScreen(services);
const splashScreen = new DragonsVsPandasSplashScreen(services);

const game = new DragonsVsPandasGame(
    services,
    gameModel,
    gameView,
    gameController,
    loadingScreen,
    splashScreen,
    gameClient
);

game.start();

declare global {
    interface Window {
        game: DragonsVsPandasGame;
        services: GameServices;
        PIXI: typeof PIXI;
    }
}

//@ts-ignore
(globalThis as any).__PIXI_APP__ = application.app;

window.PIXI = PIXI;
window.game = game;
window.services = services;
